import React, { useMemo, useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import SortableTable from '../SortableTable';
import GIQuestionHeader from './GIQuestionHeader';
import { safeParseDate } from '../../utils/dateUtils';
import { generateAIAnalysis, retrieveExistingAnalysis, formatAnalysisDate } from '../../utils/aiAnalysisUtils';

const GILongTextQuestion = ({ questionId, questionText, answers, fetchWithAuth, campaignId, currentUser, isCollapsed = false, onToggleCollapse }) => {
  const [activeTab, setActiveTab] = useState('analysis');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [ownerName, setOwnerName] = useState(null);
  const [answersCount, setAnswersCount] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [isLoadingExistingAnalysis, setIsLoadingExistingAnalysis] = useState(false);
  const [analysisSource, setAnalysisSource] = useState(null); // 'cached' ou 'generated'
  const [analysisTimestamp, setAnalysisTimestamp] = useState(null); // Date de génération/mise à jour
  const [isExporting, setIsExporting] = useState(false);
  const analysisContentRef = useRef(null);

  // États pour la modale des occurrences
  const [selectedWord, setSelectedWord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);



  const isAdmin = currentUser?.isadmin || currentUser?.issuperadmin;
  const isSuperAdmin = !!currentUser?.issuperadmin;

  // Préparer les données pour le tableau
  const tableData = useMemo(() => {
    if (!answers || !answers.length) return [];

    const textAnswers = [];

    answers.forEach(answer => {
      if (answer.Answers[questionId]) {
        // Obtenir le nom du promoteur selon le format disponible
        let promoterName;
        if (answer.PromoterName) {
          promoterName = answer.PromoterName;
        } else {
          promoterName = 'Promoteur inconnu';
        }

        // Gérer les valeurs qui pourraient être des objets
        let answerValue = answer.Answers[questionId];

        // Si c'est un objet, le convertir en string
        if (typeof answerValue === 'object' && answerValue !== null) {
          // Log pour debugger les cas étranges
          console.warn('⚠️ OBJET DÉTECTÉ dans question texte long:', {
            questionId,
            questionType: 'Long Text (type 6)',
            originalValue: answerValue,
            valueType: typeof answerValue,
            isArray: Array.isArray(answerValue),
            keys: Object.keys(answerValue),
            surveySentId: answer.SurveySentId,
            promoter: answer.PromoterName || 'Promoteur inconnu',
            answerDate: answer.AnswerDate
          });

          if (Array.isArray(answerValue)) {
            answerValue = answerValue.join(', ');
          } else {
            answerValue = JSON.stringify(answerValue);
          }
        }

        textAnswers.push({
          id: `${answer.SurveySentId}-${questionId}`,
          value: answerValue,
          promoter: promoterName,
          date: safeParseDate(answer.AnswerDate)
        });
      }
    });

    return textAnswers;
  }, [questionId, answers]);

  // Récupérer l'analyse existante lors du montage du composant
  useEffect(() => {
    const fetchExisting = async () => {
      // Vérifier qu'on a les paramètres nécessaires et des données à analyser
      if (!campaignId || !questionId || tableData.length === 0) {
        return;
      }

      setIsLoadingExistingAnalysis(true);

      try {
        const result = await retrieveExistingAnalysis({
          fetchWithAuth,
          campaignId,
          questionId
        });

        if (result) {
          setAnalysis(result.result);
          setOwnerName(result.owner_name);
          setAnswersCount(result.answers_count);
          setShowAnalysis(true);
          setAnalysisSource('cached');
          setAnalysisTimestamp(result.timestamp);
          console.log(`Analyse existante récupérée pour la campagne ${campaignId}, question ${questionId}`);
        } else {
          console.log(`Aucune analyse existante pour la campagne ${campaignId}, question ${questionId}`);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'analyse existante:', error);
        // Ne pas afficher d'erreur à l'utilisateur, c'est juste un échec de récupération
      } finally {
        setIsLoadingExistingAnalysis(false);
      }
    };

    fetchExisting();
  }, [campaignId, questionId, tableData.length, fetchWithAuth]);

  // Fonction pour lancer l'analyse IA
  const handleAnalyze = async () => {
    if (tableData.length === 0) {
      setAnalysisError("Aucune réponse à analyser");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const result = await generateAIAnalysis({
        responses: tableData,
        fetchWithAuth,
        campaignId,
        questionId,
        force: !!analysis // Si une analyse existe déjà, forcer la régénération
      });

      setAnalysis(result.result);
      setOwnerName(result.owner_name);
      setAnswersCount(result.answers_count);
      setShowAnalysis(true);
      setAnalysisSource('generated');
      setAnalysisTimestamp(result.timestamp);
    } catch (error) {
      console.error('Erreur lors de l\'analyse IA:', error);
      setAnalysisError(error.message || "Erreur lors de l'analyse");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Préparer les données pour l'analyse des occurrences
  const wordOccurrences = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];

    // Mots à exclure (mots vides français et anglais) - uniquement les mots de plus de 3 caractères
    const stopWords = new Set([
      'être', 'faire', 'aller', 'venir', 'voir', 'dire', 'prendre', 'donner', 'mettre',
      'dans', 'avec', 'pour', 'sans', 'sous', 'sont', 'vers', 'chez', 'entre', 'contre', 'depuis',
      'cette', 'notre', 'votre', 'leur', 'leurs',
      'comment', 'pourquoi', 'quand', 'combien',
      'nous', 'vous', 'elles',
      'mais', 'donc', 'cependant', 'néanmoins', 'toutefois', 'pourtant', 'alors', 'ainsi', 'aussi', 'encore',
      'très', 'bien', 'mieux', 'moins', 'plus', 'trop', 'assez', 'beaucoup', 'tant', 'autant',
      'peut-être', 'sûrement', 'vraiment', 'certainement',
      'been', 'being', 'have', 'does', 'will', 'would', 'could', 'should', 'might', 'must', 'shall',
      'with', 'without', 'from', 'about', 'into', 'through', 'during', 'before', 'after',
      'this', 'that', 'these', 'those', 'your', 'their',
      'they', 'them',
      'when', 'where', 'what', 'which',
      'very', 'much', 'many', 'more', 'most', 'less', 'least', 'some', 'both', 'each', 'every',
      'never', 'always', 'often', 'sometimes', 'usually', 'here', 'there', 'then',
      'fait', 'avons', 'peut'
    ]);

    // Compter les occurrences des mots
    const wordCount = {};
    const wordVerbatims = {}; // Pour stocker les verbatims contenant chaque mot

    tableData.forEach(item => {
      if (!item.value || typeof item.value !== 'string') return;

      // Nettoyer le texte : enlever la ponctuation et diviser en mots
      const cleanText = item.value
        .toLowerCase()
        .replace(/[^\w\s\u00C0-\u017F]/g, ' ') // Garder les lettres, espaces et accents
        .replace(/\s+/g, ' ') // Normaliser les espaces
        .trim();

      const words = cleanText.split(' ').filter(word =>
        word.length > 3 && // Mots de plus de 3 caractères
        !stopWords.has(word) && // Pas dans les mots vides
        !/^\d+$/.test(word) // Pas un nombre pur
      );

      words.forEach(word => {
        if (!wordCount[word]) {
          wordCount[word] = 0;
          wordVerbatims[word] = [];
        }
        wordCount[word]++;

        // Ajouter le verbatim s'il n'est pas déjà présent pour ce mot
        if (!wordVerbatims[word].some(v => v.id === item.id)) {
          wordVerbatims[word].push({
            id: item.id,
            value: item.value,
            promoter: item.promoter,
            date: item.date
          });
        }
      });
    });

    // Convertir en tableau et trier par occurrences (top 30)
    return Object.entries(wordCount)
      .map(([word, count]) => ({
        word,
        count,
        verbatims: wordVerbatims[word]
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 30);
  }, [tableData]);

  // Handler pour ouvrir la modale avec les verbatims d'un mot
  const handleWordClick = (wordData) => {
    setSelectedWord(wordData);
    setIsModalOpen(true);
  };

  // Handler pour fermer la modale
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedWord(null);
  };

  // Fonction pour exporter en texte
  const exportAsText = () => {
    if (!analysis) return;

    const timestamp = new Date().toLocaleString('fr-FR');
    const content = `ANALYSE IA - QUESTION LONGUE
=====================================

Question: ${questionText}
ID Question: ${questionId}
Nombre de réponses: ${tableData.length}
Date d'export: ${timestamp}
${ownerName ? `Analysé par: ${ownerName}` : ''}

=====================================

${analysis}

=====================================
Rapport généré par Globe BI - Analyse IA
`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analyse-question-${questionId}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Fonction pour exporter en PDF
  const exportAsPDF = async () => {
    if (!analysis) return;

    setIsExporting(true);
    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxLineWidth = pageWidth - 2 * margin;

      let yPosition = margin;

      // Helper function to add text with automatic line wrapping
      const addWrappedText = (text, fontSize = 12, isBold = false) => {
        doc.setFontSize(fontSize);
        if (isBold) {
          doc.setFont(undefined, 'bold');
        } else {
          doc.setFont(undefined, 'normal');
        }

        const lines = doc.splitTextToSize(text, maxLineWidth);

        lines.forEach(line => {
          if (yPosition > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += fontSize * 0.5;
        });

        yPosition += 5;
      };

      // Title
      addWrappedText('ANALYSE IA - QUESTION LONGUE', 16, true);
      yPosition += 10;

      // Metadata
      addWrappedText(`Question: ${questionText}`, 10);
      addWrappedText(`ID Question: ${questionId}`, 10);
      addWrappedText(`Nombre de réponses: ${tableData.length}`, 10);
      addWrappedText(`Date d'export: ${new Date().toLocaleString('fr-FR')}`, 10);
      if (ownerName) {
        addWrappedText(`Analysé par: ${ownerName}`, 10);
      }
      yPosition += 10;

      // Analysis content
      const plainTextAnalysis = analysis
        .replace(/#{1,6}\s*(.+)/g, '$1')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/- (.+)/g, '• $1')
        .replace(/^\s*$/gm, '')
        .replace(/\n{3,}/g, '\n\n');

      addWrappedText(plainTextAnalysis, 11);

      // Footer
      if (yPosition > doc.internal.pageSize.getHeight() - 40) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text('Rapport généré par Globe BI - Analyse IA', margin, doc.internal.pageSize.getHeight() - 15);

      doc.save(`analyse-question-${questionId}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    }
    setIsExporting(false);
  };

  // Fonction pour exporter en image
  const exportAsImage = async () => {
    if (!analysisContentRef.current) return;

    setIsExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(analysisContentRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analyse-question-${questionId}-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error('Erreur lors de l\'export image:', error);
      alert('Erreur lors de l\'export image. Veuillez réessayer.');
    }
    setIsExporting(false);
  };

  // Définir les colonnes pour le tableau SortableTable
  const columns = [
    {
      key: 'promoter',
      label: 'Promoteur',
      render: (item) => item.promoter
    },
    {
      key: 'value',
      label: 'Réponse',
      render: (item) => (
        <div className="max-w-lg">
          {item.value ? (
            <div className="whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200">
              {item.value}
            </div>
          ) : (
            <span className="italic text-gray-500">Réponse vide</span>
          )}
        </div>
      )
    },
    {
      key: 'date',
      label: 'Date',
      render: (item) => item.date ? item.date.toLocaleDateString('fr-FR') : 'Date invalide',
      sortFunction: (a, b, direction) => {
        const dateA = safeParseDate(a.date);
        const dateB = safeParseDate(b.date);

        return direction === 'ascending' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
      }
    }
  ];

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <GIQuestionHeader
        questionId={questionId}
        questionText={questionText}
        questionType="Texte long"
        badgeLabel="Long"
        adminBadge={isAdmin}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
      />

      {!isCollapsed && (
        <>
      {/* Tabs */}
      <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex">
          <button
            onClick={() => setActiveTab('analysis')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'analysis'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Analyse
          </button>
          <button
            onClick={() => setActiveTab('occurrences')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'occurrences'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            Occurrences
          </button>
          <button
            onClick={() => setActiveTab('detail')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 ${
              activeTab === 'detail'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            <svg className="w-4 h-4 hidden sm:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Détails
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'analysis' ? (
          <div className="space-y-4">
            {/* Header avec infos et boutons d'analyse */}
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {tableData.length} réponse{tableData.length > 1 ? 's' : ''} textuelle{tableData.length > 1 ? 's' : ''}
                {isLoadingExistingAnalysis && (
                  <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                    Chargement de l'analyse...
                  </span>
                )}
                {!showAnalysis && analysis && !isLoadingExistingAnalysis && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                    • Analyse disponible
                  </span>
                )}
                {/* Indicateur si de nouvelles réponses sont disponibles */}
                {analysis && answersCount !== null && tableData.length > answersCount && (
                  <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">
                    • {tableData.length - answersCount} nouvelle{tableData.length - answersCount > 1 ? 's' : ''} réponse{tableData.length - answersCount > 1 ? 's' : ''}
                  </span>
                )}
              </h4>
              <div className="flex gap-2">
                {showAnalysis && (
                  <button
                    onClick={() => setShowAnalysis(false)}
                    className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Masquer l'analyse
                  </button>
                )}
                {!showAnalysis && analysis && (
                  <button
                    onClick={() => setShowAnalysis(true)}
                    className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-colors flex items-center gap-1"
                  >
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Afficher l'analyse
                  </button>
                )}
                {/* Bouton de régénération - Superadmin uniquement, quand une analyse existe déjà sans nouvelles réponses */}
                {isSuperAdmin && analysis && !(answersCount !== null && tableData.length > answersCount) && (
                  <button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing || isLoadingExistingAnalysis || tableData.length === 0}
                    className={`px-3 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                      isAnalyzing || isLoadingExistingAnalysis || tableData.length === 0
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-purple-500 text-white hover:bg-purple-600'
                    }`}
                    title="Relancer l'analyse IA (superadmin)"
                  >
                    {isAnalyzing ? (
                      <>
                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyse...
                      </>
                    ) : (
                      <>
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Régénérer
                      </>
                    )}
                  </button>
                )}
                {/* Bouton d'analyse - Afficher seulement si pas d'analyse OU si nouvelles réponses disponibles */}
                {(!analysis || (answersCount !== null && tableData.length > answersCount)) && (
                  <button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing || isLoadingExistingAnalysis || tableData.length === 0}
                    className={`px-3 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                      isAnalyzing || isLoadingExistingAnalysis || tableData.length === 0
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                        : analysis && answersCount !== null && tableData.length > answersCount
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {isAnalyzing ? (
                      <>
                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyse...
                      </>
                    ) : isLoadingExistingAnalysis ? (
                      <>
                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Chargement...
                      </>
                    ) : (
                      <>
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        {analysis && answersCount !== null && tableData.length > answersCount ? 'Mettre à jour l\'analyse' : 'Analyser'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Affichage des erreurs */}
            {analysisError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3">
                <p className="text-red-700 dark:text-red-300 text-sm">{analysisError}</p>
              </div>
            )}

            {/* Affichage de l'analyse */}
            {showAnalysis && analysis ? (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4" ref={analysisContentRef}>
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium text-white dark:text-blue-100 bg-blue-900 dark:bg-blue-900/20 px-2 py-1 rounded-lg">
                    ✨ Analyse IA des réponses
                  </h5>
                  <div className="flex items-center gap-2">
                    {/* Export buttons */}
                    <button
                      onClick={exportAsText}
                      disabled={isExporting}
                      className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-1 disabled:opacity-50"
                      title="Exporter en texte"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      TXT
                    </button>

                    <button
                      onClick={exportAsPDF}
                      disabled={isExporting}
                      className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800 transition-colors flex items-center gap-1 disabled:opacity-50"
                      title="Exporter en PDF"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      PDF
                    </button>

                    <button
                      onClick={exportAsImage}
                      disabled={isExporting}
                      className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-800 transition-colors flex items-center gap-1 disabled:opacity-50"
                      title="Exporter en image"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      PNG
                    </button>

                    {isExporting && (
                      <div className="flex items-center gap-1">
                        <svg className="animate-spin h-3 w-3 text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-xs text-gray-600 dark:text-gray-400">Export...</span>
                      </div>
                    )}

                    {analysisSource && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        analysisSource === 'cached'
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                      }`}>
                        {analysisSource === 'cached' ? '💾 Sauvegardée' : '🆕 Générée'}
                      </span>
                    )}
                    {analysisTimestamp && (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        🕒 {formatAnalysisDate(analysisTimestamp)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Informations sur l'analyse */}
                <div className="mb-3 p-3 bg-blue-100 dark:bg-blue-800/30 rounded-lg">
                  <div className="flex flex-wrap items-center gap-4 text-sm text-blue-800 dark:text-blue-200">
                    {ownerName && (
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="font-medium">Analysée par:</span>
                        <span>{ownerName}</span>
                      </div>
                    )}
                    {answersCount !== null && (
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="font-medium">Réponses analysées:</span>
                        <span>{answersCount}</span>
                        {tableData.length > answersCount && (
                          <span className="text-orange-600 dark:text-orange-400 font-medium">
                            (+ {tableData.length - answersCount} nouvelle{tableData.length - answersCount > 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                    )}
                    {analysisTimestamp && (
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">Date:</span>
                        <span>{formatAnalysisDate(analysisTimestamp)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Avertissement si l'analyse est ancienne */}
                {analysisSource === 'cached' && analysisTimestamp && (() => {
                  const date = safeParseDate(analysisTimestamp);
                  const now = new Date();
                  const diffInDays = date ? Math.floor((now - date) / (1000 * 60 * 60 * 24)) : 0;

                  if (diffInDays >= 7) {
                    return (
                      <div className="mb-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-xs">
                        <span className="text-yellow-800 dark:text-yellow-200">
                          ⚠️ Cette analyse date de plus d'une semaine. Considérez la régénérer si de nouvelles réponses ont été ajoutées.
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Avertissement si de nouvelles réponses sont disponibles */}
                {answersCount !== null && tableData.length > answersCount && (
                  <div className="mb-3 p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded text-xs">
                    <span className="text-orange-800 dark:text-orange-200">
                      🔄 {tableData.length - answersCount} nouvelle{tableData.length - answersCount > 1 ? 's' : ''} réponse{tableData.length - answersCount > 1 ? 's' : ''} disponible{tableData.length - answersCount > 1 ? 's' : ''} depuis cette analyse. Cliquez sur "Mettre à jour l'analyse" pour inclure les nouvelles données.
                    </span>
                  </div>
                )}

                <div className="prose prose-sm dark:prose-invert max-w-none text-left">
                  <ReactMarkdown
                    components={{
                      // Hiérarchie typographique marquée pour bien distinguer les blocs
                      h1: ({children}) => <h1 className="text-left text-2xl font-extrabold mt-6 mb-4 first:mt-0 text-blue-950 dark:text-blue-50">{children}</h1>,
                      h2: ({children}) => <h2 className="text-left text-lg font-bold mt-8 mb-3 first:mt-0 pl-3 border-l-4 border-blue-400 dark:border-blue-500 text-blue-950 dark:text-blue-50">{children}</h2>,
                      h3: ({children}) => <h3 className="text-left text-[15px] font-semibold mt-4 mb-1.5 text-blue-800 dark:text-blue-200">{children}</h3>,
                      p: ({children}) => <p className="text-left mb-3 leading-relaxed text-blue-900/90 dark:text-blue-100/90">{children}</p>,
                      ul: ({children}) => <ul className="text-left list-disc pl-5 mb-4 space-y-1.5 marker:text-blue-400 dark:marker:text-blue-500">{children}</ul>,
                      ol: ({children}) => <ol className="text-left list-decimal pl-5 mb-4 space-y-1.5 marker:text-blue-500 marker:font-semibold">{children}</ol>,
                      li: ({children}) => <li className="text-left leading-relaxed text-blue-900/90 dark:text-blue-100/90 pl-1">{children}</li>,
                      strong: ({children}) => <strong className="font-bold text-blue-950 dark:text-blue-50">{children}</strong>,
                      em: ({children}) => <em className="italic text-blue-800 dark:text-blue-200">{children}</em>,
                      blockquote: ({children}) => <blockquote className="border-l-4 border-blue-300 dark:border-blue-600 pl-3 my-3 italic text-blue-800 dark:text-blue-200">{children}</blockquote>,
                      // Séparateur discret : simple espace, la hiérarchie des titres suffit à découper
                      hr: () => <hr className="my-6 border-0 border-t border-dashed border-blue-200/50 dark:border-blue-800/40" />
                    }}
                  >
                    {analysis}
                  </ReactMarkdown>
                </div>
              </div>
            ) : (
              // Message par défaut quand aucune analyse n'est affichée
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {tableData.length === 0 ? (
                  <p>Aucune réponse à analyser</p>
                ) : !analysis ? (
                  <p>Cliquez sur "Analyser" pour générer une analyse IA des réponses</p>
                ) : (
                  <p>Cliquez sur "Afficher l'analyse" pour voir l'analyse disponible</p>
                )}
              </div>
            )}
          </div>
        ) : activeTab === 'occurrences' ? (
          // Tab Occurrences - Analyse des mots les plus fréquents
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Top 30 des mots les plus fréquents
              </h4>
              <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-1 px-3 rounded-full text-sm">
                {wordOccurrences.length} mot{wordOccurrences.length > 1 ? 's' : ''} trouvé{wordOccurrences.length > 1 ? 's' : ''}
              </div>
            </div>

            {wordOccurrences.length > 0 ? (
              <div className="h-72">
                <SortableTable
                  key={`table-${questionId}-occurrences`}
                  data={wordOccurrences}
                  columns={[
                    {
                      key: 'word',
                      label: 'Mot',
                      render: (item) => (
                        <span className="font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline">
                          {item.word}
                        </span>
                      )
                    },
                    {
                      key: 'count',
                      label: 'Occurrences',
                      render: (item) => (
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{item.count}</span>
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-20">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{
                                width: `${Math.min(100, (item.count / Math.max(...wordOccurrences.map(w => w.count))) * 100)}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      )
                    }
                  ]}
                  emptyMessage="Aucun mot trouvé"
                  exportable={true}
                  exportFilename={`question-${questionId}-occurrences.csv`}
                  onRowClick={handleWordClick}
                  initialSortBy={{ key: 'count', direction: 'descending' }}
                />
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>Aucun mot trouvé dans les réponses</p>
              </div>
            )}
          </div>
        ) : (
          // Tab Détails - Tableau des réponses
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {tableData.length} réponse{tableData.length > 1 ? 's' : ''} textuelle{tableData.length > 1 ? 's' : ''}
            </h4>
            <div className="h-72">
              <SortableTable
                key={`table-${questionId}`}
                data={tableData}
                columns={columns}
                emptyMessage="Aucune réponse pour cette question"
                exportable={true}
                exportFilename={`question-${questionId}-reponses.csv`}
                initialSortBy={{ key: 'date', direction: 'descending' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Modale pour afficher les verbatims contenant le mot sélectionné */}
      {isModalOpen && selectedWord && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseModal}
            ></div>

            {/* Centrage vertical */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            {/* Contenu de la modale */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="w-full">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                        Verbatims contenant le mot "{selectedWord.word}"
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-1 px-3 rounded-full text-sm">
                          {selectedWord.count} occurrence{selectedWord.count > 1 ? 's' : ''}
                        </span>
                        <button
                          onClick={handleCloseModal}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Liste des verbatims */}
                    <div className="max-h-96 overflow-y-auto">
                      <div className="space-y-3">
                        {selectedWord.verbatims.map((verbatim, index) => (
                          <div key={verbatim.id || index} className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                              <span className="font-medium">{verbatim.promoter}</span>
                              {verbatim.date && (
                                <span className="ml-2">• {verbatim.date.toLocaleDateString('fr-FR')}</span>
                              )}
                            </div>
                            <div className="text-gray-800 dark:text-gray-200">
                              {/* Mettre en évidence le mot recherché */}
                              {verbatim.value.split(new RegExp(`(\\b${selectedWord.word}\\b)`, 'gi')).map((part, i) =>
                                part.toLowerCase() === selectedWord.word.toLowerCase() ? (
                                  <mark key={i} className="bg-yellow-200 dark:bg-yellow-600 px-1 rounded">
                                    {part}
                                  </mark>
                                ) : part
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

        </>
      )}
    </div>
  );
};

export default GILongTextQuestion;